import React, { useState } from 'react';
import OrdersService from '@api/Orders/OrderCtrl';
import { Order, OrderStatus } from '@api/Orders/Order';
import { PaymentMethod } from '@api/Orders/OrderPayments';
import { needsPaymentBeforeComplete } from '@api/Orders/OrderPaymentGate';
import { normalizeRouteUrl } from '@api/Orders/RouteUrl';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage, showAlertWarning } from '@utils/Alerts/Message';
import { getOrderShortNumber } from '@utils/OrdersBoard';
import useOpenModal from '@hooks/useOpenModal';
import useConfirmationModal from '@hooks/useConfirmationModal';
import { BoardActionKind } from '@screens/Orders/components/OrderBoardCard';
import { OrdersView } from '@screens/Orders/components/OrdersViewSwitch';

interface UseOrderActionsParams {
    ordersService: OrdersService;
    currentOrder: Order | undefined;
    setCurrentOrder: React.Dispatch<React.SetStateAction<Order | undefined>>;
    routeUrlDraft: string;
    navigateToPosForOrder: (order: Order, orderId: string | undefined) => boolean;
    onConfirmCloseOrdersByTable: (order?: Order) => Promise<void>;
    onChangeView: (nextView: OrdersView) => void;
}

// ============================================================================
// Las acciones de `/orders` que MUEVEN un pedido: cambiar su estado, cobrarlo,
// completarlo, cancelarlo, mandarlo en ruta — desde el detalle de la lista y
// desde el tablero de cocina.
//
// 🚨 Aquí vive el ÚNICO ejecutor del cierre (`completarPedido`) y el gate del
// cobro delante (`pedirCobroYCompletar`). `completePaths.test.ts` LEE ESTE
// ARCHIVO y fija que no haya otra puerta: si mueves estas funciones, mueve la
// prueba con ellas y conserva cada aserción.
// ============================================================================
const useOrderActions = ({
    ordersService,
    currentOrder,
    setCurrentOrder,
    routeUrlDraft,
    navigateToPosForOrder,
    onConfirmCloseOrdersByTable,
    onChangeView
}: UseOrderActionsParams) => {
    const modalCancel = useConfirmationModal();
    const modalRegisterPayment = useOpenModal();

    const onSendMessageTicket = async () => {
        showLoader();
        try {
            if (currentOrder?.id) {
                // A mano: el server salta el apagado del POS pero no el general.
                const { skipped } = await ordersService.sendMessageTicket(currentOrder.id, true);
                if (skipped) {
                    showAlertMessage('Este aviso está apagado en Notificaciones → WhatsApp; no se envió.');
                }
            }
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onChangeStatusOrder = async (newStatus: OrderStatus) => {
        showLoader();
        try {
            if (currentOrder?.id) {
                // Se pasa el MISMO pedido como anterior a propósito: esta
                // escritura no cambia nada (el estado lo mueve `updateStatus`,
                // que ya deja su propio renglón). Sin el anterior, la bitácora
                // registraría un "modificó este pedido" vacío en cada cambio de
                // estado hecho desde la lista.
                await ordersService.updateOrder({ ...currentOrder }, currentOrder);
                // ⚠️ **Mover el estado a `Completed` ES completar el pedido**, y
                // por lo tanto pasa por el mismo gate del cobro que los otros dos
                // caminos. Este era el TERCERO que se escapaba: el gate se puso
                // sobre los llamadores de `completeOrder`, pero `updateStatus` la
                // llama POR DENTRO (`OrderServ.ts`, `case Completed`), así que
                // este camino no aparecía en esa búsqueda. Medido en producción
                // el 2026-09-12: el modal no se abrió ni una vez en dos días de
                // operación, el selector se usó 13 veces, y el único pedido que
                // quedó sin registro de cobro salió justo por aquí. Son los dos
                // botones "Completar pedido" del panel de detalle — el de un
                // pickup listo y el de un delivery en ruta—, que son de la WEB y
                // por eso son los que más fácil llegan sin cobrar.
                if (newStatus === OrderStatus.Completed) {
                    hideLoader();
                    await pedirCobroYCompletar(currentOrder);
                    return;
                }
                await ordersService.updateStatus(currentOrder.id, newStatus);
                setCurrentOrder({ ...currentOrder, status: newStatus });
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    // Delivery SIN Uber Direct: otra plataforma (con link) o repartidor local
    // (sin link). El link es opcional y se guarda junto con el cambio de estado;
    // ver `OrderServ.routeOrder`.
    const onRouteOrder = async () => {
        if (!currentOrder?.id) return;
        showLoader();
        try {
            const routeUrl = normalizeRouteUrl(routeUrlDraft);
            await ordersService.routeOrder(currentOrder.id, routeUrl);
            setCurrentOrder({
                ...currentOrder,
                status: OrderStatus.Route,
                routeUrl: routeUrl ?? currentOrder.routeUrl
            });
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    // Completar un pedido pasa SIEMPRE por aquí, y aquí es donde se pide el cobro
    // si el pedido todavía debe algo (ver `OrderPaymentGate`).
    //
    // ⚠️ El pedido que se está cobrando vive en su PROPIO estado y no en
    // `currentOrder`: desde el tablero se completa con un clic en la tarjeta, sin
    // que ese pedido esté seleccionado — y `currentOrder` además se reemplaza
    // entero con cada entrega del listener (misma lección que `routeUrlDraft`).
    const [pedidoPorCobrar, setPedidoPorCobrar] = useState<{ order: Order } | undefined>(undefined);

    /**
     * Cierra el pedido: `completeOrder`, el ÚNICO ejecutor del cierre en esta
     * pantalla. Hasta el 2026-09-30 había dos vías (`updateStatus(Completed)` con
     * reseña y `completeOrder` sin ella) y el WhatsApp de reseña dependía de la
     * pantalla desde la que se cerraba. Hoy la reseña la decide `completeOrder`
     * (solo pedidos de la app a domicilio), así que las dos vías eran la misma.
     */
    const completarPedido = async (order: Order) => {
        showLoader();
        try {
            await ordersService.completeOrder(order.id as string);
            if (currentOrder && currentOrder.id === order.id) {
                setCurrentOrder({ ...currentOrder, status: OrderStatus.Completed });
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    /**
     * Completar, pidiendo el cobro cuando falta registrarlo.
     *
     * No es un candado: el modal ofrece "Completar sin registrar el cobro" a la
     * vista (decisión de Irving — *"que no sea bloqueante, que se pueda
     * omitir"*). Lo que cambia es que omitir sea una decisión con rastro y no un
     * descuido silencioso: era así como salían ~1 a 3 pedidos al día, y en
     * agosto costó reparar 47 mesas a mano.
     */
    const pedirCobroYCompletar = async (order: Order | undefined) => {
        if (!order?.id) return;
        if (needsPaymentBeforeComplete(order)) {
            setPedidoPorCobrar({ order });
            modalRegisterPayment.open();
            return;
        }
        await completarPedido(order);
    };

    const onRegisterPayment = async (method: PaymentMethod, amount: number) => {
        const pendiente = pedidoPorCobrar;
        setPedidoPorCobrar(undefined);
        if (!pendiente?.order?.id) return;
        showLoader();
        try {
            // El cobro va ANTES de completar: `completeOrder` lee el efectivo del
            // pedido ya guardado para crear su cash-flow.
            await ordersService.registerCollectedPayment(pendiente.order.id, method, amount);
        } catch (error: any) {
            showAlertError(error);
            hideLoader();
            return;
        }
        hideLoader();
        await completarPedido(pendiente.order);
    };

    const onSkipPayment = async () => {
        const pendiente = pedidoPorCobrar;
        setPedidoPorCobrar(undefined);
        if (!pendiente?.order?.id) return;
        // El rastro de que se cerró sin decir cómo se cobró: es justo lo que no
        // se podía saber cuando hubo que repararlo a mano.
        try {
            await ordersService.logCompletedWithoutPayment(pendiente.order.id);
        } catch {
            // La bitácora observa, nunca bloquea: si falla, el pedido se cierra igual.
        }
        await completarPedido(pendiente.order);
        showAlertWarning('El pedido se completó SIN registrar el cobro — queda en el informe como pedido sin pago');
    };

    // El botón del detalle de la lista: nunca mandó el WhatsApp de reseña.
    const onCompleteOrder = async () => {
        await pedirCobroYCompletar(currentOrder);
    };

    const onCancelOrder = async () => {
        showLoader();
        try {
            if (currentOrder?.id) {
                await ordersService.cancelOrder(currentOrder.id);
                setCurrentOrder({
                    ...currentOrder,
                    status: OrderStatus.Cancelled
                });
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onConfirmCancelOrder = () => {
        if (currentOrder?.id) {
            modalCancel.setModalData('Cancelar pedido', 'Estas seguro de cancelar esta orden?');
        }
    };

    /**
     * Acciones del tablero de cocina. El tablero NO inventa transiciones: cada
     * caso llama al mismo servicio que ya usaba la lista para ese tipo de pedido,
     * solo que a un clic y sobre cualquier tarjeta (no solo la seleccionada).
     */
    const onBoardAction = async (order: Order, kind: BoardActionKind) => {
        if (!order.id) return;

        // Acciones que no tocan Firestore
        switch (kind) {
            case 'openInList':
                setCurrentOrder(order);
                onChangeView('list');
                return;
            case 'chargeInPos':
                navigateToPosForOrder(order, order.id);
                return;
            case 'closeTable':
                setCurrentOrder(order);
                await onConfirmCloseOrdersByTable(order);
                return;
            case 'cancel':
                setCurrentOrder(order);
                modalCancel.setModalData(
                    'Cancelar pedido',
                    `Estas seguro de cancelar el pedido #${getOrderShortNumber(order)}?`
                );
                return;
        }

        showLoader();
        try {
            switch (kind) {
                case 'markPaid':
                    await ordersService.updateStatus(order.id, OrderStatus.Paid);
                    break;
                case 'start':
                    await ordersService.updateStatus(order.id, OrderStatus.Preparing);
                    break;
                case 'ready':
                    await ordersService.updateStatus(order.id, OrderStatus.Ready);
                    break;
                case 'complete':
                    // Mismo camino que la lista, incluido el paso del cobro: la
                    // tarjeta del tablero era la otra puerta por la que un pedido
                    // se cerraba sin registrar con qué se pagó.
                    hideLoader();
                    await pedirCobroYCompletar(order);
                    return;
                case 'stepBack':
                    // Regresar un paso NO puede pasar por updateStatus: volver a
                    // "Pagado" dispararía otra vez el WhatsApp de pago al cliente y al
                    // responsable. updateOrder es la escritura plana sin efectos
                    // secundarios (la misma que usa ModalViewOrder).
                    await ordersService.updateOrder(
                        {
                            ...order,
                            status: order.status === OrderStatus.Ready ? OrderStatus.Preparing : OrderStatus.Paid,
                            lastUpdate: new Date()
                        },
                        order
                    );
                    break;
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    return {
        modalCancel,
        modalRegisterPayment,
        pedidoPorCobrar,
        onSendMessageTicket,
        onChangeStatusOrder,
        onRouteOrder,
        onRegisterPayment,
        onSkipPayment,
        onCompleteOrder,
        onCancelOrder,
        onConfirmCancelOrder,
        onBoardAction
    };
};

export default useOrderActions;
